// Typed client for the five template routes.

import type { FieldOptions, TemplateDocument } from '@template-filler/core';
import { config } from '../config.js';
import { getAccessToken } from '../auth/cognito.js';

export interface TemplateSummary {
  templateId: string;
  title: string;
  placeholderNames: string[];
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface Template extends TemplateSummary {
  body: TemplateDocument;
  // Feature 004: present only when the template has at least one constrained
  // (dropdown) field; absence means every field fills as free text.
  fieldOptions?: FieldOptions;
}

export type ApiErrorCode =
  | 'unauthorized'
  | 'not_found'
  | 'validation_failed'
  | 'version_conflict'
  | 'payload_too_large'
  | 'stored_document_invalid'
  | 'internal_error'
  | 'network_error';

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details: { path: string; message: string }[] | undefined;

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string,
    details?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** The session expired mid-work; the caller must preserve unsaved changes. */
  get isSessionExpired(): boolean {
    return this.code === 'unauthorized';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getAccessToken();
  if (!token) {
    throw new ApiError(401, 'unauthorized', 'Your session has ended. Sign in to continue.');
  }

  let response: Response;
  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, {
      ...init,
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        authorization: `Bearer ${token}`,
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check your connection.');
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => undefined)) as
    | { code?: ApiErrorCode; message?: string; details?: { path: string; message: string }[] }
    | undefined;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.code ?? 'internal_error',
      payload?.message ?? 'Something went wrong.',
      payload?.details,
    );
  }

  return payload as T;
}

export const api = {
  listTemplates: () =>
    request<{ templates: TemplateSummary[] }>('/templates').then((r) => r.templates),

  getTemplate: (templateId: string) => request<Template>(`/templates/${templateId}`),

  createTemplate: (input: { title: string; body: TemplateDocument; fieldOptions?: FieldOptions }) =>
    request<Template>('/templates', { method: 'POST', body: JSON.stringify(input) }),

  updateTemplate: (
    templateId: string,
    input: { title: string; body: TemplateDocument; version: number; fieldOptions?: FieldOptions },
  ) =>
    request<Template>(`/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  deleteTemplate: (templateId: string) =>
    request<void>(`/templates/${templateId}`, { method: 'DELETE' }),
};
